import * as React from "react";
import Uppy from "@uppy/core";
import Tus from "@uppy/tus";
import Dashboard from "@uppy/dashboard";
import Spanish from "@uppy/locales/lib/es_ES";
import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import '../App.css';

const { useEffect, useState } = React;

const generateUniqueFileName = (originalName: string): string => {
  const timestamp = Date.now();
  const extension = originalName.includes('.') 
    ? originalName.split('.').pop() 
    : '';
  const baseName = originalName.includes('.') 
    ? originalName.substring(0, originalName.lastIndexOf('.')) 
    : originalName;
  
  const cleanBaseName = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_');
  
  return `${cleanBaseName}_${timestamp}${extension ? `.${extension}` : ''}`;
};

const useUppyWithSupabase = () => {
  const [uppy] = useState(() =>
    new Uppy({
      restrictions: {
        maxFileSize: 20 * 1024 * 1024, // 20MB in bytes
        maxNumberOfFiles: 1,
        allowedFileTypes: ['image/*']
      },
      autoProceed: true,
      locale: Spanish
    })
  );

  useEffect(() => {
    const initializeUppy = async () => {
      const params = new URLSearchParams(window.location.search);
      const authToken = params.get('token');
      const modeloId = params.get('modeloid');
      const returnUrl = params.get('returnUrl');
      const numero = params.get('numero') || '1';
      const bucketName = 'users';

      if (!authToken) {
        console.error('No authentication token provided');
        return;
      }

      if (!modeloId) {
        console.error('No modelo ID provided');
        return;
      }

      uppy.use(Tus, {
        endpoint: `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/upload/resumable`,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: {
          authorization: `Bearer ${authToken}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        chunkSize: 6 * 1024 * 1024,
        allowedMetaFields: [
          "bucketName",
          "objectName",
          "contentType",
          "cacheControl",
        ],
        onError: (error: Error) => {
          console.error("Upload error:", error);
        },
      });

      let uploadedUrl: string | null = null;

      uppy.on('file-added', (file: any) => {
        const uniqueFileName = generateUniqueFileName(file.name);
        file.meta.originalName = file.name;
        file.name = uniqueFileName;
        file.meta = {
          ...file.meta,
          bucketName,
          objectName: uniqueFileName,
          contentType: file.type,
        };
      });

      uppy.on('upload-success', async (file: any, response: any) => {
        console.log('Upload success - File:', file);
        console.log('Upload success - Response:', response);

        const publicUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/${bucketName}/${file.name}`;
        uploadedUrl = publicUrl;

        try {
          const functionResponse = await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/rpc/modelo_foto_perfil`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
                'Apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
              },
              body: JSON.stringify({
                p_numero: parseInt(numero),
                p_imagen_url: publicUrl,
                p_modeloid: modeloId
              }),
            }
          );

          if (!functionResponse.ok) {
            throw new Error('Failed to update profile photo');
          }

          if (returnUrl) {
            const baseReturnUrl = decodeURIComponent(returnUrl);
            const separator = baseReturnUrl.includes('?') ? '&' : '?';
            const imageParam = encodeURIComponent(uploadedUrl);
            const finalReturnUrl = `${baseReturnUrl}${separator}imageurl=${imageParam}`;
            console.log('Redirecting to:', finalReturnUrl);
            window.location.href = finalReturnUrl;
          }
        } catch (error) {
          console.error('Error calling Supabase function:', error);
        }
      });
    };

    initializeUppy();

    return () => (uppy as any).close();
  }, [uppy]);

  return uppy;
};

const FileUploaderFotosPerfil = () => {
  const [error, setError] = useState('');
  const uppy = useUppyWithSupabase();

  useEffect(() => {
    uppy.use(Dashboard, {
      inline: true,
      target: "#drag-drop-area",
      height: 450,
      width: '100%',
      showProgressDetails: true,
      proudlyDisplayPoweredByUppy: false,
      note: 'Sube una imagen de perfil (máximo 10MB).',
      showRemoveButtonAfterComplete: false,
      hideCancelButton: true,
      doneButtonHandler: null,
    });
  }, [uppy]);

  return (
    <div className="file-uploader-container">
      <div className="file-uploader-box">
        <div className="header-mini">
          <h1 className="upload-title">Sube tu foto de perfil</h1>
          <img src="/Logo_Monomo-02.png" alt="Logo" className="logo" />
        </div>
        <div id="drag-drop-area" />
        {error && (
          <p className="error-message">{error}</p>
        )}
      </div>
    </div>
  );
};

export default FileUploaderFotosPerfil;